import { Link, useParams } from "react-router-dom";
import {
  ArrowRight,
  BookOpen,
  Wallet,
  Receipt,
  Calculator,
  Briefcase,
  BarChart3,
  Users,
  ClipboardCheck,
  Landmark,
  FileSpreadsheet,
  LineChart,
  Phone,
  Plus,
  ShieldCheck,
} from "lucide-react";
import servicePages from "../data/servicePages";
import { getBreadcrumbs } from "../seo";
import Breadcrumbs from "../components/Breadcrumbs";
import NotFound from "./NotFound";

const slugIcons = {
  bookkeeping: BookOpen,
  payroll: Wallet,
  "sales-tax": Receipt,
  "tax-preparation": Calculator,
  "cfo-advisory": Briefcase,
  "financial-reporting": BarChart3,
  "cpa-firm-outsourcing": Users,
};
const featureIcons = [ClipboardCheck, Landmark, FileSpreadsheet, LineChart];

const stats = [
  ["9+", "Years of legacy"],
  ["500+", "Clients served"],
  ["3", "Offices: Bellevue, Surat, Ahmedabad"],
  ["ISO", "27001, 27701 and 9001 certified"],
];

export default function ServicePage({ slug: slugProp }) {
  const params = useParams();
  const slug = slugProp || params.slug;
  const page = servicePages.find((p) => p.slug === slug);
  if (!page) return <NotFound />;
  const crumbs = getBreadcrumbs(`/services/${page.slug}`);
  const related = page.related
    .map((s) => servicePages.find((p) => p.slug === s))
    .filter(Boolean);
  const idx = servicePages.indexOf(page);
  const bandImage = servicePages[(idx + 1) % servicePages.length].image;
  const HeroIcon = slugIcons[page.slug] || ShieldCheck;

  return (
    <div className="sp-page sv">
      <section className="sv-hero">
        <div className="sv-hero-glow" aria-hidden="true"></div>
        <div className="sp-container sv-hero-grid">
          <div className="sv-hero-copy">
            <Breadcrumbs items={crumbs} />
            <p className="sv-pill">
              <HeroIcon className="w-4 h-4" /> {page.eyebrow}
            </p>
            <h1 className="sv-h1">{page.h1}</h1>
            <p className="sv-lead">{page.intro}</p>
            <div className="sv-actions">
              <Link to="/contact" className="sv-btn-primary">
                Talk to our team <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="tel:+14252216660" className="sv-btn-ghost">
                <Phone className="w-4 h-4" /> +1 425 221 6660
              </a>
            </div>
          </div>
          <div className="sv-hero-media">
            <img
              src={page.image}
              alt={page.imageAlt}
              width="800"
              height="800"
              className="sv-hero-img"
            />
            <div className="sv-hero-badge">
              <HeroIcon className="w-6 h-6" />
              <span>{page.name}</span>
            </div>
          </div>
        </div>
        <div className="sp-container">
          <dl className="sv-stats">
            {stats.map(([n, l]) => (
              <div key={l} className="sv-stat">
                <dt>{n}</dt>
                <dd>{l}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="sv-section">
        <div className="sp-container sv-feat-grid">
          <div className="sv-feat-media">
            <img
              src={bandImage}
              alt=""
              width="800"
              height="800"
              loading="lazy"
              className="sv-band-img"
            />
          </div>
          <div>
            <p className="sv-kicker">What is included</p>
            <h2 className="sv-h2 sv-h2-sm">Everything the service covers</h2>
            <ul className="sv-rows">
              {page.features.map(([title, desc], i) => {
                const Icon = featureIcons[i % featureIcons.length];
                return (
                  <li key={title}>
                    <Icon className="w-5 h-5" aria-hidden="true" />
                    <div>
                      <h3>{title}</h3>
                      <p>{desc}</p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </section>

      {page.sections.map((section, i) => (
        <section
          key={section.h2}
          className={`sv-block ${i % 2 === 0 ? "sv-block-tint" : ""}`}
        >
          <div className="sp-container sv-block-grid">
            <div className="sv-block-head">
              <span className="sv-num">{String(i + 1).padStart(2, "0")}</span>
              <h2 className="sv-h2 sv-h2-sm">{section.h2}</h2>
            </div>
            <div className="sv-prose">
              {section.paragraphs.map((para, j) => (
                <p key={j} className={j === 0 ? "sv-prose-first" : ""}>
                  {para}
                </p>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="sv-process">
        <div className="sp-container">
          <p className="sv-kicker sv-kicker-light">The process</p>
          <h2 className="sv-h2 sv-h2-light">How we work with you</h2>
          <ol className="sv-steps">
            {page.process.map(([title, desc], i) => (
              <li key={title} className="sv-step">
                <span className="sv-step-no">{i + 1}</span>
                <h3>{title}</h3>
                <p>{desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="sv-section">
        <div className="sp-container sv-faq-grid">
          <div>
            <p className="sv-kicker">FAQ</p>
            <h2 className="sv-h2">Frequently asked questions</h2>
            <div className="sv-callout">
              <p>Have a question that is not here?</p>
              <a href="tel:+14252216660">
                <Phone className="w-4 h-4" /> +1 425 221 6660
              </a>
              <Link to="/contact">Send us a message</Link>
            </div>
          </div>
          <div className="sv-faq">
            {page.faqs.map(([q, a]) => (
              <details key={q}>
                <summary>
                  <span>{q}</span>
                  <Plus className="w-5 h-5" aria-hidden="true" />
                </summary>
                <p>{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="sv-section">
        <div className="sp-container">
          <p className="sv-kicker">Keep exploring</p>
          <h2 className="sv-h2">Related services</h2>
          <div className="sv-related">
            {related.map((r) => (
              <Link key={r.slug} to={`/services/${r.slug}`} className="sv-rel-row">
                <h3>{r.name}</h3>
                <p>{r.description}</p>
                <ArrowRight className="w-5 h-5" aria-hidden="true" />
              </Link>
            ))}
          </div>
          <p className="sp-allservices">
            See all{" "}
            <Link to="/services">accounting, tax and payroll services</Link>.
          </p>
        </div>
      </section>

      <section className="sv-cta">
        <div className="sp-narrow">
          <h2>Ready to talk about {page.name}?</h2>
          <p>Call +1 425 221 6660 or send us a note and we will get back to you.</p>
          <div className="sv-actions sv-actions-center">
            <Link to="/contact" className="sv-btn-white">
              Contact Our Experts
            </Link>
            <a href="tel:+14252216660" className="sv-btn-ghost">
              <Phone className="w-4 h-4" /> Call now
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
